/**
 * qcLedger — BQC5 outcomes: pass, or send to repair, by the SET.
 *
 * A WIP received at BQC4 does not clear QC as one unit. The realistic case
 * (Mikko, 2026-08-11): DS 4361 goes out with 10 WIPs / 20 sets; the
 * subcontractor brings back 9 WIPs / 18 sets, the 10th WIP still with them
 * for a later delivery; QC then finds 17 of those 18 sets pass and 1 needs
 * repair. That 1 set is not lost and not scrap — it goes back for rework and
 * is inspected again later, on its own, possibly split further.
 *
 * So a WIP's QC state is not a single latest outcome, it is a RUNNING TALLY:
 * every scan records how many sets it covers and what happened to them, and
 * a WIP is "done" only once passed sets reach its qty. `qcTally()` is that
 * sum, computed fresh every time — the same derive-don't-store posture as
 * receivingLedger's slip reconciliation, and for the same reason: a stored
 * status can drift from the scans that are supposed to justify it, a summed
 * one cannot.
 *
 * REPAIR IS A HOLD, NOT A STATE. A "repair" scan means N sets are out for
 * rework; there is no separate "returned from repair" event. When those sets
 * come back, the station undoes the repair scan that was holding them (soft —
 * it stays as history) and logs a fresh scan with whatever this inspection
 * finds. That is the same soft-undo idiom every other scan log in this module
 * already uses, reused here instead of inventing a second state machine.
 *
 * The SharePoint shape is UNCHANGED from the first cut (wipId, qty, outcome,
 * qcBy, qcAt) — this is a change to what the numbers MEAN, not a new column.
 * One inspection that finds a mixed result is two rows, one per outcome, the
 * same way a slip's WIPs are one row each rather than one wide row.
 */
import type { ISlip } from "../../lib/sp/DispatchService";

export type QcOutcome = "pass" | "repair";

/**
 * Which path a `repair` outcome takes. "subcontractor" is the original,
 * major-defect path — the set leaves the building (markReturnedToSewer,
 * sewerReturns.ts). "inhouse" is a minor defect QC judges fixable on the
 * floor, usually to make a due date too close to afford a subcontractor
 * round-trip; it never goes through Return to Sewer. Undefined on an
 * existing row (written before this distinction existed) reads as
 * "subcontractor" — see repairKindOf — so old data keeps its original
 * behavior without a backfill.
 */
export type RepairKind = "subcontractor" | "inhouse";

export interface IQcScan {
  spId: number;
  wipId: string;
  /** How many sets this event covers — not the WIP's total. */
  qty: number;
  outcome: QcOutcome;
  qcBy: string;
  qcAt: string;
  /** Set when the scan was undone — a mis-scan, or sets pulled back in for
   * re-inspection once repair work on them is done. Soft — the row stays as
   * history either way. */
  undoneBy?: string;
  undoneAt?: string;
  /** Set only on a `repair` outcome, once the failed set has actually been
   * handed to the subcontractor — a deliberate, separate action from
   * recording the failure itself (see sewerReturns.ts). A repair outcome
   * with no returnedAt is a set QC has found faulty but not yet sent out;
   * only once this is set does the round start counting as "with the
   * sewer" toward a received-back match. */
  returnedBy?: string;
  returnedAt?: string;
  /** Only meaningful on a `repair` outcome — see RepairKind. */
  repairKind?: RepairKind;
  /** The defect category (repairRemarks.ts), free text kept only for
   * whatever the taxonomy doesn't yet cover. Advisory, like qty and outcome
   * — QC intake never blocks on it being set. */
  remark?: string;
  /**
   * Sup Approval — the review layer over an in-house repair (see
   * RepairKind). Set once a supervisor has looked at the categorization and
   * signed off. This is NOT a gate: the scan already counts in qcTally the
   * moment it's recorded, same as any other outcome; approval only marks
   * that someone with authority reviewed the call to keep it in-house
   * instead of returning it to the subcontractor.
   */
  supApprovedBy?: string;
  supApprovedAt?: string;
  /** A supervisor reviewed an in-house repair and disagreed with the call —
   * recorded for accountability, same non-gating posture as approval. Does
   * not undo or reclassify the scan; a human still has to act on it. */
  supRejectedBy?: string;
  supRejectedAt?: string;
  /** Set once this scan has been folded into a BQC5 or REPAIR outbox export
   * — the same meaning IReceivingScan.syncedToWorkbookAt carries. Absent
   * means the workbook has never been told about it. See qcOutbox.ts. */
  syncedToWorkbookAt?: string;
  /**
   * The QC userform row this scan was IMPORTED from — `wb:` for a pass,
   * `wr:` for a rework, the same keys the payroll run derives.
   *
   * ABSENT MEANS AN OPERATOR RECORDED IT at the station, and that is the whole
   * point of the field: the list is a union of two origins, and only the
   * imported half is the importer's to rewrite. See qcWorkbookImport.ts.
   */
  sourceKey?: string;
}

/** A repair scan's kind, defaulting absent data to "subcontractor" — the
 * only path that existed before this distinction did. */
export function repairKindOf(s: IQcScan): RepairKind {
  return s.repairKind === "inhouse" ? "inhouse" : "subcontractor";
}

/** An in-house repair QC has not yet been reviewed by a supervisor. */
export function isInhouseRepair(s: IQcScan): boolean {
  return s.outcome === "repair" && repairKindOf(s) === "inhouse";
}

export type SupApprovalStatus = "n/a" | "pending" | "approved" | "rejected";

/** Where an in-house repair stands with its supervisor review — "n/a" for
 * anything that isn't an in-house repair (a subcontractor repair or a pass
 * never goes through this at all). */
export function supApprovalStatusOf(s: IQcScan): SupApprovalStatus {
  if (!isInhouseRepair(s)) return "n/a";
  if (s.supRejectedAt) return "rejected";
  if (s.supApprovedAt) return "approved";
  return "pending";
}

/** Every active in-house repair scan still awaiting a supervisor's review,
 * newest first — the Sup Approval tab's own queue. */
export function pendingSupApprovals(scans: readonly IQcScan[]): IQcScan[] {
  return active(scans)
    .filter(s => supApprovalStatusOf(s) === "pending")
    .sort((a, b) => b.qcAt.localeCompare(a.qcAt));
}

/** The active (non-undone) scans only — an undone scan did not happen. */
function active(scans: readonly IQcScan[]): IQcScan[] {
  return scans.filter(s => !s.undoneAt);
}

export interface IQcTally {
  wipId: string;
  /** The WIP's total sets — what everything below has to add up to. */
  qty: number;
  /** Cumulative sets passed, active scans only. Capped at qty. */
  passed: number;
  /** Sets currently out for repair — inspected once, not yet resolved. */
  repair: number;
  /** Sets never inspected at all. qty − passed − repair. */
  awaiting: number;
  /** Has this WIP been touched by QC at all. */
  inspected: boolean;
  /** Every set has passed. */
  complete: boolean;
}

/**
 * Sum the active scans for one WIP into where its sets currently stand.
 *
 * `passed` and `repair` are each capped against `qty` — a duplicate or
 * over-reported scan must not read as "120% passed", the same discipline
 * receivingLedger's summary already holds for WIP counts.
 */
export function qcTally(wipId: string, qty: number, scans: readonly IQcScan[]): IQcTally {
  const id = (wipId ?? "").trim();
  const total = Math.max(0, qty || 0);
  let passed = 0;
  let repair = 0;
  for (const s of active(scans)) {
    if (s.wipId !== id) continue;
    const q = Math.max(0, s.qty || 0);
    if (s.outcome === "pass") passed += q;
    else repair += q;
  }
  passed = Math.min(passed, total);
  repair = Math.min(repair, Math.max(0, total - passed));
  return {
    wipId: id,
    qty: total,
    passed,
    repair,
    awaiting: Math.max(0, total - passed - repair),
    inspected: passed + repair > 0,
    complete: total > 0 && passed >= total,
  };
}

export interface ISlipQcSummary {
  /** WIPs every set of which has passed — not merely inspected, and not
   *  merely received. A WIP with one set still in repair, or one never
   *  received at all, counts the same way here: not yet clean. */
  passed: number;
  total: number;
  reconciled: boolean;
  /** WIPs with at least one set not yet passed. */
  incompleteWipIds: string[];
}

/**
 * How much of a slip has actually cleared QC, by WIP — BQC5's own reading
 * of the same dispatch a slip's `wipIds` names, one hop past
 * receivingLedger's slipReceiveSummary.
 *
 * THIS is the figure that decides a subcontractor's payroll, not
 * slipReceiveSummary's — a set BQC4 marked received is not yet earned; it
 * only counts once QC passes it. `qtyOf` gives each WIP's own full
 * dispatched qty (the ceiling `reconciled` is measured against);
 * `receivedQtyOf` gives how much of that WIP has actually reached this
 * station yet — a WIP still partly with the subcontractor cannot read
 * "passed" for sets that were never inspected because they never arrived.
 */
export function slipQcSummary(
  slip: ISlip,
  scans: readonly IQcScan[],
  qtyOf: (wipId: string) => number,
  receivedQtyOf: (wipId: string) => number,
  /** How to tally one WIP. Defaults to walking `scans`; a caller holding an
   *  index by WIP passes `qcTallyIn` here and skips the walk — see qcIndex. */
  tallyFor: (wipId: string, qty: number) => IQcTally = (id, qty) => qcTally(id, qty, scans)
): ISlipQcSummary {
  const incompleteWipIds: string[] = [];
  let passed = 0;
  for (const id of slip.wipIds) {
    const full = Math.max(0, qtyOf(id) || 0);
    if (full > 0 && tallyFor(id, receivedQtyOf(id)).passed >= full) passed++;
    else incompleteWipIds.push(id);
  }
  const total = slip.wipIds.length;
  return { passed, total, reconciled: total > 0 && incompleteWipIds.length === 0, incompleteWipIds };
}

export interface ISlipQcSets {
  dispatchedSets: number;
  passedSets: number;
  outstandingSets: number;
}

/**
 * The same final-judgment reconciliation as slipQcSummary, in sets rather
 * than WIPs — the reading BQC5's own DS ↔ DR panel shows. `outstandingSets`
 * only shrinks once a set actually passes: never received, received but not
 * yet inspected, and out for repair all read as outstanding alike. A repair
 * round that comes back and passes on re-inspection folds straight back in —
 * qcTally sums the ACTIVE scans fresh every time, so there is no separate
 * "returned from repair" bookkeeping here to get out of sync.
 */
export function slipQcSetsSummary(
  slip: ISlip,
  scans: readonly IQcScan[],
  qtyOf: (wipId: string) => number,
  receivedQtyOf: (wipId: string) => number,
  /** See slipQcSummary. */
  tallyFor: (wipId: string, qty: number) => IQcTally = (id, qty) => qcTally(id, qty, scans)
): ISlipQcSets {
  let dispatchedSets = 0, passedSets = 0;
  for (const id of slip.wipIds) {
    dispatchedSets += Math.max(0, qtyOf(id) || 0);
    passedSets += tallyFor(id, receivedQtyOf(id)).passed;
  }
  return { dispatchedSets, passedSets, outstandingSets: dispatchedSets - passedSets };
}

/**
 * The workbook-imported facts a HISTORICAL slip's QC reconciliation is built
 * from — receivedQty is BQC4's own fact (already imported for the Receiving
 * side, see receivingLedger.ts), qcPassedQty/qcPassedAt are BQC5's own
 * (2026-08-19 design conversation with Mikko — the direct mirror of the
 * receiving-side fix, since a historical slip's WIPs can never get a live QC
 * scan any more than they can a live receiving scan).
 */
export interface IWorkbookQcInfo {
  receivedQty?: number;
  qcPassedQty?: number;
  qcPassedAt?: string;
}

/**
 * historicalQcTally / historicalSlipQcSummary / historicalSlipQcSetsSummary —
 * the QC-side twin of receivingLedger's historicalReceiveTally family, the
 * same one narrow, deliberate exception to "reconciliation is scans-only",
 * scoped to slip.historical.
 *
 * A historical slip's WIPs structurally can never get an iSyn2_QcScans entry
 * — the pre-app QC process that inspected them predates this app entirely —
 * so scans-only reconciliation would read every one of them as permanently
 * "awaiting QC" even for sets the workbook's own BQC5 sheet already shows
 * passed years ago. `qcPassedQty` (parseProductionGridWorkbook.ts's own
 * capped/reconciled BQC5 figure, the same discipline receivedQty already
 * gets) stands in for the scan-summed `passed` here. `repair` is always 0 —
 * the workbook's BQC5 REPAIR sheet logs repair EVENTS with dates, not a
 * live "still outstanding" quantity ledger, so there is no clean workbook
 * source for "currently in rework" the way there is for "already passed";
 * this only changes what an unpassed set is labelled as, never the
 * pass/fail-complete verdict, which is all reconciliation actually needs.
 */
export function historicalQcTally(wipId: string, receivedQty: number, info: IWorkbookQcInfo | undefined): IQcTally {
  const id = (wipId ?? "").trim();
  const total = Math.max(0, receivedQty || 0);
  const passed = Math.min(Math.max(0, info?.qcPassedQty ?? 0), total);
  return {
    wipId: id, qty: total, passed, repair: 0,
    awaiting: Math.max(0, total - passed),
    inspected: passed > 0,
    complete: total > 0 && passed >= total,
  };
}

export function historicalSlipQcSummary(
  slip: ISlip,
  wipOf: (wipId: string) => IWorkbookQcInfo | undefined,
  qtyOf: (wipId: string) => number
): ISlipQcSummary {
  const incompleteWipIds: string[] = [];
  let passed = 0;
  for (const id of slip.wipIds) {
    const full = Math.max(0, qtyOf(id) || 0);
    const info = wipOf(id);
    const receivedCeiling = Math.max(0, info?.receivedQty ?? 0);
    if (full > 0 && historicalQcTally(id, receivedCeiling, info).passed >= full) passed++;
    else incompleteWipIds.push(id);
  }
  const total = slip.wipIds.length;
  return { passed, total, reconciled: total > 0 && incompleteWipIds.length === 0, incompleteWipIds };
}

export function historicalSlipQcSetsSummary(
  slip: ISlip,
  wipOf: (wipId: string) => IWorkbookQcInfo | undefined,
  qtyOf: (wipId: string) => number
): ISlipQcSets {
  let dispatchedSets = 0, passedSets = 0;
  for (const id of slip.wipIds) {
    dispatchedSets += Math.max(0, qtyOf(id) || 0);
    const info = wipOf(id);
    const receivedCeiling = Math.max(0, info?.receivedQty ?? 0);
    passedSets += historicalQcTally(id, receivedCeiling, info).passed;
  }
  return { dispatchedSets, passedSets, outstandingSets: dispatchedSets - passedSets };
}

/** How many separate repair rounds are outstanding for this WIP right now —
 * ordinarily 0 or 1, but nothing stops a WIP being split into more. */
export function reworkCount(wipId: string, scans: readonly IQcScan[]): number {
  const id = (wipId ?? "").trim();
  if (!id) return 0;
  return active(scans).filter(s => s.wipId === id && s.outcome === "repair").length;
}

export interface IRepairHistoryRow {
  spId: number;
  wipId: string;
  /** Sets this event covers. */
  qty: number;
  /** The sewer whose bundle this was — the caller's own lookup (typically
   * the dispatch slip's sewer), since a QC scan itself carries no sewer. */
  sewer: string;
  qcBy: string;
  qcAt: string;
  /** True once the hold this event created has been cleared (re-inspected
   * or otherwise resolved) — undone here means "resolved", not "never
   * happened", so a resolved event still belongs in the history. */
  resolved: boolean;
  /** This WIP's Nth repair finding, 1-based, in the order QC actually found
   * them (oldest first) — a WIP rejected three times shows 1/2/3, not three
   * indistinguishable rows a reader has to count themselves. */
  round: number;
}

/**
 * Every set BQC5 has ever sent to repair, sewer-attributed, newest first —
 * the quality-accountability record qcTally alone cannot give: it only
 * says a WIP's CURRENT state, not the running history of who made the
 * pieces that failed. Includes resolved (soft-undone) events on purpose —
 * a repaired-and-passed set is still a set that failed once.
 */
export function repairHistory(
  scans: readonly IQcScan[],
  sewerOf: (wipId: string) => string
): IRepairHistoryRow[] {
  const byWip = new Map<string, IQcScan[]>();
  for (const s of scans) {
    if (s.outcome !== "repair") continue;
    const arr = byWip.get(s.wipId) ?? [];
    arr.push(s);
    byWip.set(s.wipId, arr);
  }
  const rows: IRepairHistoryRow[] = [];
  for (const [wipId, events] of byWip) {
    const ordered = [...events].sort((a, b) => a.qcAt.localeCompare(b.qcAt));
    ordered.forEach((s, i) => {
      rows.push({
        spId: s.spId, wipId, qty: Math.max(0, s.qty || 0),
        sewer: sewerOf(wipId).trim() || "—", qcBy: s.qcBy, qcAt: s.qcAt,
        resolved: !!s.undoneAt, round: i + 1,
      });
    });
  }
  return rows.sort((a, b) => b.qcAt.localeCompare(a.qcAt));
}

export interface ISewerRepairTotal {
  sewer: string;
  /** Total sets sent to repair, ever — resolved or still outstanding. */
  qty: number;
  /** Distinct WIPs that contributed at least one of those sets. */
  wips: number;
}

/** repairHistory(), rolled up to one row per sewer — the scorecard reading
 * of the same events, sorted worst-first. */
export function repairTotalsBySewer(rows: readonly IRepairHistoryRow[]): ISewerRepairTotal[] {
  const m = new Map<string, { qty: number; wips: Set<string> }>();
  for (const r of rows) {
    const cur = m.get(r.sewer) ?? { qty: 0, wips: new Set<string>() };
    cur.qty += r.qty;
    cur.wips.add(r.wipId);
    m.set(r.sewer, cur);
  }
  return [...m.entries()]
    .map(([sewer, v]) => ({ sewer, qty: v.qty, wips: v.wips.size }))
    .sort((a, b) => b.qty - a.qty);
}

export interface IQcQueueItem {
  wipId: string;
  /** The WIP's total sets, e.g. from Production Grid data. */
  qty: number;
}

/** Which received WIPs still have sets outstanding — never inspected, or
 * held in repair. Bounded by what BQC4 actually received, same as before. */
export function qcQueue(received: readonly IQcQueueItem[], scans: readonly IQcScan[]): string[] {
  return received.filter(r => !qcTally(r.wipId, r.qty, scans).complete).map(r => r.wipId);
}

export type QcVerdict = "ok" | "not-received" | "already-complete" | "exceeds-remaining";

export interface IQcCheck {
  /** Always true — QC intake never refuses a scan, see the file header. */
  ok: true;
  verdict: QcVerdict;
  /** Shown as a warning banner; empty when verdict is "ok". */
  reason: string;
}

/**
 * Should this inspection raise a warning, and what should it say?
 *
 * Same advise-never-block posture as receivingLedger.canReceive: a WIP QC'd
 * before it was logged as received, one already fully passed, or a count
 * bigger than what is actually awaiting inspection — all still get written,
 * with the operator told rather than stopped.
 */
export function canQc(
  wipId: string,
  qty: number,
  submitQty: number,
  receivedWipIds: ReadonlySet<string>,
  scans: readonly IQcScan[]
): IQcCheck {
  const warn = (verdict: QcVerdict, reason: string): IQcCheck => ({ ok: true, verdict, reason });
  const id = (wipId ?? "").trim();
  if (!id) return warn("not-received", "No WIP scanned.");

  if (!receivedWipIds.has(id)) {
    return warn("not-received", `${id} has no receiving scan at BQC4 — recording the QC outcome anyway, but check the WIP ID.`);
  }
  const tally = qcTally(id, qty, scans);
  if (tally.complete) {
    return warn("already-complete", `${id} already has all ${tally.qty} set${tally.qty === 1 ? "" : "s"} passed — this will record more.`);
  }
  if (submitQty > tally.awaiting) {
    return warn(
      "exceeds-remaining",
      `Only ${tally.awaiting} of ${id}'s sets are awaiting inspection — recording ${submitQty} anyway, but check the count.`
    );
  }
  return { ok: true, verdict: "ok", reason: "" };
}

/**
 * Repair evidence per sewer, in the shape `deriveSewerMetrics` takes — event
 * count and the sets those events cover, keyed by upper-case ledger name.
 *
 * Counts EVERY repair finding, resolved ones included, for the same reason
 * repairHistory does: a set that was repaired and then passed is still a set
 * that failed once, and a quality score that forgets its own fixes measures
 * how diligent the repair loop is rather than how good the sewing was.
 *
 * The event count is what `subconGrading` scores on, per the grading sheet;
 * the set count rides along for reporting.
 */
export function repairEvidenceBySewer(
  rows: readonly IRepairHistoryRow[]
): Map<string, { count: number; sets: number }> {
  const m = new Map<string, { count: number; sets: number }>();
  for (const r of rows) {
    const key = (r.sewer ?? "").toUpperCase().trim();
    if (!key || key === "—") continue;
    const cur = m.get(key) ?? { count: 0, sets: 0 };
    cur.count += 1;
    cur.sets += Math.max(0, r.qty || 0);
    m.set(key, cur);
  }
  return m;
}
